var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { copyToNumber3, MulticastDelegate1, MulticastDelegate3 } from "flavr-common";
import { busService, busServiceMain } from "../bus";
import { mainScene } from "../rendering/mainScene";
import { Matrix4, Vector3 } from "flavr-math";
import { now } from "@messages/timing";
import { renderer } from "../rendering/RendererGlobals";
import { camera } from "../rendering/camera";
import { BystanderView } from "./BystanderView";
import { WebXRPresentingMessage, WebXREnabledMessage, BoundedReferenceSpaceMessage } from "@messages/xr/WebXRMessages";
import { capabilitiesService } from "../rendering/CapabilitiesService";
import Stats from "stats-gl";
import { ScenePostprocessingRenderer } from "../rendering/postprocessing/ScenePostprocessingRenderer";
import { preferencesService } from "../preferences/PreferencesService";
import { getGtaoSettings, isGtaoPreference } from "../rendering/postprocessing/GtaoSettings";
import { getBloomSettings, getBloomResolutionScale, isBloomPreference } from "../rendering/postprocessing/BloomSettings";
import { getSmaaQuality } from "../rendering/postprocessing/SmaaSettings";
import { DoubleSide, Mesh, MeshBasicMaterial, PlaneGeometry, Scene, Vector3 as ThreeVector3 } from "three";
import { roomTransitionLoadingState } from "../rendering/RoomTransitionLoadingState";
// if adaptive pixelation is on, we want this framerate when not presenting
const TARGET_FPS_BROWSER = 65;
// this is (1 / #num frames to consider for fps median)
const ADAPTIVE_SPEED = 0.1;
const DEBUG_LOG_FRAME_TIME = false;
class RenderManager {
    constructor() {
        this.xrManager = renderer.xr;
        this.postprocessingRenderer = new ScenePostprocessingRenderer(renderer);
        this.transitionOverlayScene = new Scene();
        this.transitionOverlayMaterial = new MeshBasicMaterial({
            color: roomTransitionLoadingState.getClearColor(),
            transparent: true,
            opacity: 0,
            depthTest: false,
            depthWrite: false,
            side: DoubleSide,
            toneMapped: false
        });
        this.transitionOverlay = new Mesh(new PlaneGeometry(200, 200), this.transitionOverlayMaterial);
        this.transitionOverlayDirection = new ThreeVector3();
        this.bystanderView = new BystanderView();
        this.isXRPresenting$ = new MulticastDelegate3();
        this.onBoundedReferenceSpaceChanged$ = new MulticastDelegate1();
        this.currentViewDirection = new Vector3();
        this.currentViewDirectionArray = [0, 0, 0];
        this.currentProjectionScreenMatrix = new Matrix4();
        this.targetFPS = 60;
        this.frameCount = 0;
        this.lastFrameAt = 0;
        this.meanFrameDuration = 1000 / this.targetFPS;
        this.trackingAreaSize = new Vector3();
        this.trackingAreaMin = new Vector3();
        this.trackingAreaMax = new Vector3();
        this.isEmulatedXRDevice = false;
        this.showBystanderView = false;
        this.streamBystanderView = false;
        this.handleBoundedReferenceSpaceMessage = (msg) => {
            this.trackingAreaSize.fromArray(msg.areaSize);
            this.trackingAreaMin.fromArray(msg.areaMin);
            this.trackingAreaMax.fromArray(msg.areaMax);
            this.onBoundedReferenceSpaceChanged$.invoke(this.trackingAreaSize);
        };
        this.initRenderer();
        this.transitionOverlayScene.add(this.transitionOverlay);
        const connection = busService.connection("WebVR");
        connection.subscribeByType(WebXRPresentingMessage, (msg) => {
            this.isXRPresenting$.invoke(msg.isPresenting, msg.position, msg.direction);
        });
        connection.subscribeByType(WebXREnabledMessage, (msg) => {
            this.targetFPS = msg.enabled ? capabilitiesService.targetFpsHmd : TARGET_FPS_BROWSER;
        });
        connection.subscribeByType(BoundedReferenceSpaceMessage, this.handleBoundedReferenceSpaceMessage);
        preferencesService.onPreferenceChanged.add((key) => __awaiter(this, void 0, void 0, function* () { return isGtaoPreference(key) || isBloomPreference(key) || key === "smaaQuality" || key === "usePostEffects"; }));
        // create stats panel
        this.gpuPanel = new Stats();
        // document.body.append(this.gpuPanel.dom)
    }
    init() {
        // Initial invoke: ensure that all callbacks are registered
        this.isXRPresenting$.invoke(this.isXRPresenting(), undefined, undefined);
    }
    initRenderer() {
        if (!capabilitiesService.shadow) {
            renderer.shadowMap.enabled = false;
            renderer.shadowMap.autoUpdate = false;
        }
    }
    render() {
        var _a;
        const before = this.lastFrameAt || now();
        const scene = mainScene;
        camera.perspectiveCam.getWorldDirection(this.currentViewDirection);
        copyToNumber3(this.currentViewDirection, this.currentViewDirectionArray);
        this.currentProjectionScreenMatrix.multiplyMatrices(camera.perspectiveCam.projectionMatrix, camera.perspectiveCam.matrixWorldInverse);
        if (this.gpuPanel) {
            this.gpuPanel.begin();
        }
        const usePostEffects = preferencesService.getBooleanPreferredValue("usePostEffects", false) === true;
        const useBloom = usePostEffects && preferencesService.getBooleanPreferredValue("useBloom", true) === true;
        const useGtao = usePostEffects && preferencesService.getBooleanPreferredValue("useGtao", true) === true;
        const gtaoSettings = useGtao ? getGtaoSettings(preferencesService) : undefined;
        const bloomSettings = useBloom ? getBloomSettings(preferencesService) : undefined;
        const bloomResolutionScale = getBloomResolutionScale(preferencesService);
        const smaaQuality = getSmaaQuality(preferencesService);
        this.postprocessingRenderer.render(scene, camera.perspectiveCam, usePostEffects, gtaoSettings, bloomSettings, bloomResolutionScale, smaaQuality, useBloom, useGtao);
        if (roomTransitionLoadingState.isActive()) {
            this.transitionOverlayMaterial.opacity = roomTransitionLoadingState.getClearAlpha();
            camera.perspectiveCam.getWorldPosition(this.transitionOverlay.position);
            camera.perspectiveCam.getWorldDirection(this.transitionOverlayDirection);
            this.transitionOverlay.position.addScaledVector(this.transitionOverlayDirection, 10);
            camera.perspectiveCam.getWorldQuaternion(this.transitionOverlay.quaternion);
            const autoClear = renderer.autoClear;
            renderer.autoClear = false;
            try {
                renderer.render(this.transitionOverlayScene, camera.perspectiveCam);
            }
            finally {
                renderer.autoClear = autoClear;
            }
        }
        if (this.gpuPanel) {
            this.gpuPanel.end();
        }
        if ((this.showBystanderView || this.streamBystanderView) && this.xrManager.isPresenting && !this.isEmulatedXRDevice) {
            const frameRate = ((_a = this.xrManager.getSession()) === null || _a === void 0 ? void 0 : _a.frameRate) || this.targetFPS;
            this.bystanderView.updateXRFrameTiming(before, now() - before, frameRate);
            this.bystanderView.render(this.showBystanderView, this.streamBystanderView, usePostEffects, gtaoSettings, bloomSettings, bloomResolutionScale, smaaQuality, useBloom, useGtao);
        }
        // Is calling getSession necessary here?
        // const session = renderer.xr.getSession()
        // if (session === null) {
        //   console.warn("[RenderManager] Session not available")
        // }
        const after = now();
        const renderDuration = after - before;
        // this.lastFrameAt = after;
        this.meanFrameDuration = (1 - ADAPTIVE_SPEED) * this.meanFrameDuration + ADAPTIVE_SPEED * renderDuration;
        const meanFPS = 1000 / this.meanFrameDuration;
        const targetDuration = 1000 / this.targetFPS;
        let processUntil = 0;
        if (meanFPS < this.targetFPS * 0.9) {
            if (processUntil !== 0 || this.frameCount % 100 === 0) {
                console.warn("[xr] Rendering in " + renderDuration.toFixed(2) + "ms (mean: " + this.meanFrameDuration.toFixed(2) + "ms. Thats too slow, can't throttle messages!");
            }
            // we have lots of frame drops anyway. don't throttle messages at all to avoid buffer overflow
            processUntil = 0;
        }
        else if (DEBUG_LOG_FRAME_TIME) {
            // rendering is fine. set messageProcessingUntil for messages
            const remainingTime = Math.max(0, targetDuration - renderDuration);
            processUntil = after + remainingTime;
            if (this.frameCount % 1000 === 0) {
                console.log("[xr] Rendering in " +
                    renderDuration.toFixed(2) +
                    "ms (mean: " +
                    this.meanFrameDuration.toFixed(2) +
                    "ms). Target: " +
                    targetDuration.toFixed(2) +
                    "ms remaining time: " +
                    remainingTime.toFixed(2) +
                    "ms.");
            }
        }
        busServiceMain.processBufferedMessages(processUntil);
        busServiceMain.messageCounter = 0;
        ++this.frameCount;
    }
    isXRPresenting() {
        return this.xrManager.isPresenting;
    }
    setBystanderViewEnabled(enabled) {
        this.showBystanderView = enabled;
        this.releaseBystanderViewResourcesIfUnused();
    }
    setBystanderViewStreaming(enabled) {
        this.streamBystanderView = enabled;
        this.releaseBystanderViewResourcesIfUnused();
    }
    updateRenderSize(width, height) {
        if (width <= 0 || height <= 0)
            return;
        if (this.isXRPresenting()) {
            // setSize() is blocked during XR; resize only the desktop drawing buffer, not the XR render target.
            renderer.setDrawingBufferSize(width, height, renderer.getPixelRatio());
            renderer.domElement.style.width = width + "px";
            renderer.domElement.style.height = height + "px";
        }
        else {
            renderer.setSize(width, height);
        }
        this.bystanderView.updateRenderTargetSize(width, height);
    }
    releaseBystanderViewResourcesIfUnused() {
        if (!this.showBystanderView && !this.streamBystanderView) {
            this.bystanderView.releaseResources();
        }
    }
}
export const renderManager = new RenderManager();
